import { computed, toValue } from 'vue'
import { useStore } from 'vuex'

import { openExternalUrl, openDownloadUrl } from '@/utils/external-link'
import { beaconClickout } from '@/utils/analytics-beacons'
import errorHandler from '@/plugins/error-handler'

/**
 * Resolves which URL a bookmark's "open source" action should use, and opens
 * it the way the current platform wants (in-app browser on native).
 *
 * Public pages route through the clickout-counting redirect (`outUrl`, a 302
 * on our API that preserves affiliate links); in-app surfaces use the raw
 * destination and report the click with a beacon instead. Keeping that rule
 * here means it lives in one place — it was previously duplicated across four
 * bookmark components plus the public detail view.
 *
 * Uploaded images have no destination: in-app the action downloads the stored
 * original through the api's signed link (D17/D19 — never a storage url). Only
 * the single read mints that link and it expires after an hour, so `openSource`
 * fetches the item on click instead of trusting whatever a list left on it. On
 * public pages only the uploader's opt-in public link opens, through the same
 * counting redirect.
 *
 * @param {Object|import('vue').Ref} bookmark  the bookmark (ref, getter or plain)
 * @param {Object} options
 * @param {boolean|import('vue').Ref|Function} options.directUrl
 *        true to use the raw destination (in-app surfaces), false to route
 *        through the counting redirect (public pages)
 * @returns {{ sourceUrl: import('vue').ComputedRef<string|null>, openSource: Function }}
 */
export function useBookmarkSourceUrl(bookmark, { directUrl }) {
  const store = useStore()

  const sourceUrl = computed(() => {
    const item = toValue(bookmark)
    if (!item) return null
    // Image items: the signed download link in-app (null until a single read minted one); on
    // public pages the redirect to the uploader's opt-in public link (D17) — null without one
    // keeps the arrow a no-op.
    if (item.isImage) return toValue(directUrl) ? item.fileUrl : item.outUrl
    // Both getters share the same guard, so outUrl is null exactly when
    // brandedUrl is — no fallback needed between them.
    return toValue(directUrl) ? item.brandedUrl : item.outUrl
  })

  // The same read the detail page does, so it also refreshes the item in the store.
  async function downloadOriginal(item) {
    try {
      const { fileUrl } = await store.dispatch('collectionItems/getId', { collectionId: item.ownerId, id: item.id })
      openDownloadUrl(fileUrl)
    } catch (error) {
      errorHandler.toast(error)
    }
  }

  async function openSource() {
    const item = toValue(bookmark)
    if (item?.isImage && toValue(directUrl)) await downloadOriginal(item)
    else if (sourceUrl.value) openExternalUrl(sourceUrl.value)
  }

  return { sourceUrl, openSource }
}

/**
 * Same thing for the bookmark list/card/tile/headline components, which all
 * share one `{ bookmark, publicMode, membership }` props contract. In-app
 * links are always direct — those clicks are counted with a beacon instead of
 * the redirect; the server dedups per identity per 30 minutes.
 *
 * @param {Object} props the component's props object
 */
export function useBookmarkItemSourceUrl(props) {
  const { sourceUrl, openSource: open } = useBookmarkSourceUrl(() => props.bookmark, {
    directUrl: () => !props.publicMode,
  })

  function openSource() {
    if (!props.publicMode) beaconClickout(props.bookmark)
    open()
  }

  return { sourceUrl, openSource }
}
